import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Plus, CheckCircle2,
  AlertTriangle, Clock, FileText, X,
  DollarSign, Edit2, Trash2, Save,
  Upload, Eye, Download, StopCircle
} from 'lucide-react';
import { financeService } from '../../services/financeService';
import { bankAccountService } from '../../services/bankAccountService';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from '../../components/StatusBadge';

const PAYMENT_METHODS = ['Bank Transfer', 'Cash', 'Cheque', 'Online Payment', 'Other'];
const CURRENCIES = ['PKR', 'USD', 'EUR', 'GBP', 'AED'];

// ── Milestone status icon ─────────────────────────────────────────────────
const MilestoneIcon = ({ status }) => {
  if (status === 'Received') return <CheckCircle2 className="w-5 h-5 text-green-500" />;
  if (status === 'Overdue') return <AlertTriangle className="w-5 h-5 text-red-500" />;
  if (status === 'Due') return <Clock className="w-5 h-5 text-amber-500" />;
  return <Clock className="w-5 h-5 text-gray-300" />;
};


// ── Mark Milestone Received Modal ─────────────────────────────────────────
const MarkReceivedModal = ({ milestone, contractId, onClose, onDone }) => {
  const [paymentMethod, setPaymentMethod] = useState('Bank Transfer');
  const [paidDate, setPaidDate] = useState(new Date().toISOString().split('T')[0]);
  const [amountReceived, setAmountReceived] = useState(milestone.amount != null ? String(milestone.amount) : '');
  const [bankAccountId, setBankAccountId] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const res = await bankAccountService.getAccounts();
        setBankAccounts(res.data || []);
      } catch (err) {
        setError('Failed to load bank accounts');
      } finally {
        setLoadingAccounts(false);
      }
    };
    loadAccounts();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!amountReceived || Number(amountReceived) <= 0) return setError('Enter the actual amount received.');
    if (!bankAccountId) return setError('Select which bank account this was deposited into.');

    setSubmitting(true);
    try {
      await financeService.markMilestoneReceived(contractId, milestone.milestone_id, {
        payment_method: paymentMethod,
        paid_date: paidDate,
        amount_received: Number(amountReceived),
        bank_account_id: bankAccountId,
      });
      onDone();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-md">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between font-bold text-gray-900">
          Mark Payment Received
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}
          <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-600">
            <p className="font-semibold text-gray-800">{milestone.description}</p>
            <p className="text-xs mt-1">Milestone Amount: {milestone.amount.toLocaleString()}</p>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Amount Actually Received *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amountReceived}
              onChange={(e) => setAmountReceived(e.target.value)}
              placeholder="After tax deduction, if any"
              className="w-full p-2.5 border rounded-xl text-sm bg-gray-50"
            />
            <p className="text-xs text-gray-400 mt-1">This exact amount goes into revenue — not the milestone amount above.</p>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Deposited Into Bank Account *</label>
            <select
              value={bankAccountId}
              onChange={(e) => setBankAccountId(e.target.value)}
              disabled={loadingAccounts}
              className="w-full p-2.5 border rounded-xl bg-gray-50 text-sm"
            >
              <option value="">{loadingAccounts ? 'Loading...' : 'Select account'}</option>
              {bankAccounts.map(acc => (
                <option key={acc.account_id} value={acc.account_id}>{acc.bank_name} — {acc.account_owner}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Payment Method</label>
            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full p-2.5 border rounded-xl bg-gray-50 text-sm">
              {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Payment Date</label>
            <input type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)}
              required className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-500 border border-gray-200">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-green-600 shadow-lg shadow-green-100 flex items-center justify-center gap-2">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Confirm
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
// ── Add Milestone Modal ───────────────────────────────────────────────────
const AddMilestoneModal = ({ contractId, onClose, onDone }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const fd = new FormData(e.target);
    try {
      await financeService.addMilestone(contractId, {
        description: fd.get('description'),
        due_date: fd.get('due_date'),
        amount: parseFloat(fd.get('amount'))
      });
      onDone();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-md">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between font-bold text-gray-900">
          Add Milestone
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Description</label>
            <input name="description" required placeholder="e.g. 30% upfront payment"
              className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Due Date</label>
            <input name="due_date" type="date" required
              className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Amount</label>
            <input name="amount" type="number" step="0.01" required
              className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-500 border border-gray-200">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-primary flex items-center justify-center gap-2">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Add
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Edit Contract Modal ───────────────────────────────────────────────────
const EditContractModal = ({ contract, salesReps, onClose, onDone }) => {
  const [clientName, setClientName] = useState(contract.client_name || '');
  const [projectName, setProjectName] = useState(contract.project_name || '');
  const [startDate, setStartDate] = useState(contract.start_date || '');
  const [endDate, setEndDate] = useState(contract.end_date || '');
  const [totalValue, setTotalValue] = useState(contract.total_value || '');
  const [currency, setCurrency] = useState(contract.currency || 'PKR');
  const [salesRepId, setSalesRepId] = useState(contract.sales_rep_id || '');
  const [paymentTerms, setPaymentTerms] = useState(contract.payment_terms || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    
    const selectedRep = salesReps.find(r => r.id === salesRepId);

    try {
      await financeService.updateContract(contract.contract_id, {
        client_name: clientName,
        project_name: projectName,
        start_date: startDate,
        end_date: endDate || null,
        total_value: parseFloat(totalValue),
        currency: currency,
        payment_terms: paymentTerms || null,
        sales_rep_id: salesRepId || null,
        sales_rep_name: selectedRep ? selectedRep.full_name : (contract.sales_rep_name || null)
      });
      onDone();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between font-bold text-gray-900 sticky top-0 bg-white">
          Edit Contract
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Client Name *</label>
              <input value={clientName} onChange={(e) => setClientName(e.target.value)} required
                className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Project Name *</label>
              <input value={projectName} onChange={(e) => setProjectName(e.target.value)} required
                className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Start Date *</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required
                className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-1">End Date</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Total Value *</label>
              <input type="number" value={totalValue} onChange={(e) => setTotalValue(e.target.value)} required
                className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Currency</label>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                className="w-full p-2.5 border rounded-xl bg-gray-50 text-sm">
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Sales Rep</label>
            <select value={salesRepId} onChange={(e) => setSalesRepId(e.target.value)}
              className="w-full p-2.5 border rounded-xl bg-gray-50 text-sm">
              <option value="">Unassigned</option>
              {salesReps.map(rep => (
                <option key={rep.id} value={rep.id}>{rep.full_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Payment Terms</label>
            <textarea value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} rows={2}
              placeholder="e.g. 30% upfront, 70% on delivery"
              className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
          </div>

          <div className="flex gap-3 pt-2 border-t border-gray-100">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-500 border border-gray-200">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-primary flex items-center justify-center gap-2">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              <Save className="w-4 h-4" />
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── End Contract Modal ────────────────────────────────────────────────────
const EndContractModal = ({ contract, onClose, onDone }) => {
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!endDate) return setError('End date is required.');
    if (!reason.trim()) return setError('Reason is required.');

    setSubmitting(true);
    try {
      await financeService.endContract(contract.contract_id, {
        end_date: endDate,
        reason: reason.trim(),
        description: description.trim() || null,
      });
      onDone();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-md">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between font-bold text-gray-900">
          End Contract
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-800">
            This will mark <span className="font-bold">{contract.project_name}</span> as Ended. This cannot be undone.
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">End Date *</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required
              className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Reason *</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} required
              placeholder="e.g. Client cancelled, project completed early"
              className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Description (Optional)</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
              placeholder="Any additional details..."
              className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-500 border border-gray-200">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-red-500 shadow-lg shadow-red-100 flex items-center justify-center gap-2">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Confirm End
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Main ContractDetail Component ─────────────────────────────────────────
const ContractDetail = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [salesReps, setSalesReps] = useState([]);

  // Modal states
  const [markingMilestone, setMarkingMilestone] = useState(null);
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);

  // Action states
  const [generatingInvoice, setGeneratingInvoice] = useState('');
  const [triggeringCommission, setTriggeringCommission] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [actionType, setActionType] = useState('success'); // 'success' | 'error'
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const isAdmin = user && ['super_admin', 'finance_manager', 'hr_manager'].includes(user.role);
  const canManage = user && ['super_admin', 'finance_manager'].includes(user.role);

  const loadContract = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await financeService.getContract(contractId);
      setContract(res.data);
    } catch (err) {
      setError(err.message || 'Contract not found');
    } finally {
      setLoading(false);
    }
  };

  const loadSalesReps = async () => {
    try {
      const res = await financeService.getSalesReps();
      setSalesReps(res.data || []);
    } catch (err) {
      // silent fail
    }
  };

  useEffect(() => {
    loadContract();
    loadSalesReps();
  }, [contractId]);

  // Auto-hide action message after 4 seconds
  useEffect(() => {
    if (actionMsg) {
      const t = setTimeout(() => setActionMsg(''), 4000);
      return () => clearTimeout(t);
    }
  }, [actionMsg]);

  const showMsg = (msg, type) => {
    setActionMsg(msg);
    setActionType(type || 'success');
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await financeService.uploadContractDocument(contractId, { file_base64: base64, filename: file.name });
      showMsg('Document uploaded successfully', 'success');
      loadContract();
    } catch (err) {
      showMsg(err.message || 'Failed to upload document', 'error');
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  const handlePreviewDocument = () => {
    const url = financeService.getContractDocumentPreviewUrl(contractId);
    window.open(url, '_blank');
  };

  const handleDownloadDocument = async () => {
    try {
      await financeService.downloadContractDocument(contractId, contract.document_filename);
    } catch (err) {
      showMsg(err.message || 'Failed to download document', 'error');
    }
  };

  // Generate invoice aur PDF download (SRS FIN-02)
  const handleGenerateInvoice = async (milestone) => {
    setGeneratingInvoice(milestone.milestone_id);
    try {
      const res = await financeService.generateInvoice(contract.contract_id, milestone.milestone_id);
      const invoiceId = res?.data?.invoice_id;
      showMsg('Invoice generated: ' + milestone.description, 'success');

      // PDF open karo new tab mein
      if (invoiceId) {
        const token = localStorage.getItem('access_token');
        const base = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const response = await fetch(base + '/api/finance/invoices/' + invoiceId + '/download', {
          headers: { Authorization: 'Bearer ' + token }
        });
        if (response.ok) {
          const blob = await response.blob();
          window.open(URL.createObjectURL(blob), '_blank');
        }
      }
      loadContract();
    } catch (err) {
      showMsg('Invoice error: ' + err.message, 'error');
    } finally {
      setGeneratingInvoice('');
    }
  };

  // Commission create karo — lead_id ho ya na ho (SRS SAL-02)
  const handleTriggerCommission = async () => {
    if (!contract.sales_rep_id) {
      showMsg('No sales rep assigned. Please edit contract and assign a sales rep first.', 'error');
      return;
    }
    setTriggeringCommission(true);
    try {
      await financeService.createCommissionFromContract(contract.contract_id);
      showMsg('Commission created successfully! Sales rep has been notified.', 'success');
    } catch (err) {
      if (err.message && err.message.toLowerCase().includes('already')) {
        showMsg('Commission already exists for this contract.', 'success');
      } else {
        showMsg('Commission error: ' + err.message, 'error');
      }
    } finally {
      setTriggeringCommission(false);
    }
  };

  // Contract delete karo (SRS 4.2)
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await financeService.deleteContract(contract.contract_id);
      navigate('/contracts');
    } catch (err) {
      showMsg('Delete failed: ' + err.message, 'error');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <Loader2 className="animate-spin text-primary opacity-20 w-10 h-10" />
    </div>
  );

  if (error || !contract) return (
    <div className="p-10 text-center text-gray-400">
      <FileText className="w-10 h-10 mx-auto mb-2 opacity-20" />
      <p>{error || 'Contract not found'}</p>
      <button onClick={() => navigate('/contracts')}
        className="mt-4 text-primary text-sm font-bold hover:underline">
        Back to Contracts
      </button>
    </div>
  );

  const totalReceived = contract.milestones
    .filter(m => m.status === 'Received')
    .reduce((sum, m) => sum + m.amount, 0);

  const totalPending = contract.total_value - totalReceived;
  const isEnded = contract.status === 'Ended';

  return (
    <div className="space-y-6 pb-10">
      {/* Back button */}
      <button onClick={() => navigate('/contracts')}
        className="flex items-center gap-2 text-gray-500 hover:text-primary text-sm font-bold transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Contracts
      </button>

      {/* Action message */}
      {actionMsg && (
        <div className={
          'rounded-xl px-4 py-3 text-sm font-medium border ' +
          (actionType === 'error'
            ? 'bg-red-50 border-red-200 text-red-800'
            : 'bg-green-50 border-green-200 text-green-800')
        }>
          {actionMsg}
        </div>
      )}

      {/* Header card */}
      <div className="bg-white rounded-card border border-gray-100 shadow-soft p-6 relative overflow-hidden">
        <div className={'absolute top-0 left-0 w-1.5 h-full ' + (isEnded ? 'bg-red-400' : 'bg-primary')} />
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                {contract.contract_id}
              </p>
              {isEnded && <StatusBadge status="Ended" />}
            </div>
            <h1 className="text-2xl font-black text-gray-900">{contract.project_name}</h1>
            <p className="text-gray-500 font-medium mt-1">{contract.client_name}</p>
            {contract.sales_rep_name && (
              <p className="text-xs text-primary font-bold mt-1 uppercase tracking-wider">
                Sales Rep: {contract.sales_rep_name}
              </p>
            )}
            {contract.payment_terms && (
              <p className="text-xs text-gray-400 mt-1">{contract.payment_terms}</p>
            )}
            {isEnded && contract.end_reason && (
              <div className="mt-2 bg-red-50 border border-red-100 rounded-xl p-3 text-xs text-red-700 max-w-md">
                <p className="font-bold uppercase tracking-wider text-[10px] text-red-400">Ended Reason</p>
                <p className="mt-0.5">{contract.end_reason}</p>
                {contract.end_description && <p className="mt-1 text-red-600">{contract.end_description}</p>}
              </div>
            )}
          </div>

          <div className="flex flex-col items-end gap-2">
            <p className="text-2xl font-black text-gray-900">
              {contract.total_value.toLocaleString()} {contract.currency}
            </p>
            <p className="text-xs text-gray-400">{contract.start_date} — {contract.end_date || 'No end date'}</p>

            {/* Action buttons row */}
            {canManage && (
              <div className="flex items-center gap-2 mt-1 flex-wrap justify-end">
                {/* Edit button */}
                <button
                  onClick={() => setShowEditModal(true)}
                  className="flex items-center gap-1.5 text-xs font-bold text-gray-600 bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                  <Edit2 className="w-3.5 h-3.5" />
                  Edit
                </button>

                {/* End Contract button — only while still Active */}
                {!isEnded && (
                  <button
                    onClick={() => setShowEndModal(true)}
                    className="flex items-center gap-1.5 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-100 px-3 py-1.5 rounded-lg hover:bg-amber-100 transition-colors">
                    <StopCircle className="w-3.5 h-3.5" />
                    End Contract
                  </button>
                )}

                {/* Delete button */}
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-1.5 text-xs font-bold text-red-600 bg-red-50 border border-red-100 px-3 py-1.5 rounded-lg hover:bg-red-100 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </button>

                {/* Commission button — sirf tab dikhao jab sales rep assign ho */}
                {contract.sales_rep_id && (
                  <button
                    onClick={handleTriggerCommission}
                    disabled={triggeringCommission}
                    className="flex items-center gap-1.5 text-xs font-bold text-violet-700 bg-violet-50 border border-violet-200 px-3 py-1.5 rounded-lg hover:bg-violet-100 transition-colors disabled:opacity-60">
                    {triggeringCommission
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <DollarSign className="w-3.5 h-3.5" />}
                    Create Commission
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-card border border-gray-100 shadow-soft p-4">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">Total Value</p>
          <p className="text-lg font-black text-gray-900 mt-1">
            {contract.total_value.toLocaleString()} {contract.currency}
          </p>
        </div>
        <div className="bg-white rounded-card border border-gray-100 shadow-soft p-4">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">Received</p>
          <p className="text-lg font-black text-green-600 mt-1">{totalReceived.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-card border border-gray-100 shadow-soft p-4">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">Pending</p>
          <p className="text-lg font-black text-amber-500 mt-1">{totalPending.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-card border border-gray-100 shadow-soft p-4">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">Milestones</p>
          <p className="text-lg font-black text-gray-900 mt-1">
            {contract.milestones.filter(m => m.status === 'Received').length}/{contract.milestones.length}
          </p>
        </div>
      </div>

      {/* Contract Document */}
      <div className="bg-white rounded-card border border-gray-100 shadow-soft p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-black text-gray-900 text-sm uppercase tracking-wider flex items-center gap-2">
            <FileText className="w-4 h-4" /> Contract Document
          </h2>
          {canManage && (
            <label className="flex items-center gap-1.5 text-xs font-bold text-primary bg-primary/10 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-primary/20 transition-colors">
              {uploadingDoc ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {contract.document_filename ? 'Replace' : 'Upload'}
              <input
                type="file"
                accept="application/pdf,image/*"
                className="hidden"
                onChange={handleDocumentUpload}
                disabled={uploadingDoc}
              />
            </label>
          )}
        </div>

        {contract.document_filename ? (
          <div className="flex items-center justify-between mt-3 p-3 bg-gray-50 rounded-control border border-gray-100">
            <div
              className="flex items-center gap-3 min-w-0 cursor-pointer group"
              onClick={handlePreviewDocument}
              title="Click to preview"
            >
              <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <p className="text-sm text-gray-800 truncate group-hover:text-primary group-hover:underline">{contract.document_filename}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={handlePreviewDocument} className="p-2 text-gray-400 hover:text-primary" title="Preview">
                <Eye className="w-4 h-4" />
              </button>
              <button onClick={handleDownloadDocument} className="p-2 text-gray-400 hover:text-primary" title="Download">
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400 mt-3">No document uploaded yet.</p>
        )}
      </div>

      {/* Milestones section */}
      <div className="bg-white rounded-card border border-gray-100 shadow-soft overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
          <h2 className="font-black text-gray-900 text-sm uppercase tracking-wider">
            Payment Milestones
          </h2>
          {isAdmin && (
            <button onClick={() => setShowAddMilestone(true)}
              className="flex items-center gap-1.5 text-xs font-bold text-primary bg-primary/5 border border-primary/10 px-3 py-1.5 rounded-lg hover:bg-primary/10 transition-colors">
              <Plus className="w-3.5 h-3.5" /> Add Milestone
            </button>
          )}
        </div>

        {contract.milestones.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <p className="text-sm">No milestones added yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {contract.milestones.map((m) => (
              <div key={m.milestone_id} className="px-5 py-4 flex items-center gap-4">
                <MilestoneIcon status={m.status} />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-800 text-sm">{m.description}</p>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    <span className="text-xs text-gray-500">Due: {m.due_date}</span>
                    {m.paid_date && (
                      <span className="text-xs text-green-600 font-medium">Paid: {m.paid_date}</span>
                    )}
                    <StatusBadge status={m.status} />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-black text-gray-900 text-sm">
                    {m.amount.toLocaleString()} {contract.currency}
                  </p>
                  {isAdmin && m.status !== 'Received' && (
                    <button
                      onClick={() => setMarkingMilestone(m)}
                      className="mt-1.5 text-[10px] font-bold text-green-600 bg-green-50 border border-green-100 px-2.5 py-1 rounded-lg hover:bg-green-100 transition-colors block">
                      Mark Received
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => handleGenerateInvoice(m)}
                      disabled={generatingInvoice === m.milestone_id}
                      className="mt-1 text-[10px] font-bold text-blue-600 bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-1 disabled:opacity-50 ml-auto">
                      {generatingInvoice === m.milestone_id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <FileText className="w-3 h-3" />}
                      Invoice
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Modals ── */}

      {markingMilestone && (
        <MarkReceivedModal
          milestone={markingMilestone}
          contractId={contract.contract_id}
          onClose={() => setMarkingMilestone(null)}
          onDone={() => { setMarkingMilestone(null); loadContract(); }}
        />
      )}

      {showAddMilestone && (
        <AddMilestoneModal
          contractId={contract.contract_id}
          onClose={() => setShowAddMilestone(false)}
          onDone={() => { setShowAddMilestone(false); loadContract(); }}
        />
      )}

      {showEditModal && (
        <EditContractModal
          contract={contract}
          salesReps={salesReps}
          onClose={() => setShowEditModal(false)}
          onDone={() => { setShowEditModal(false); loadContract(); showMsg('Contract updated successfully', 'success'); }}
        />
      )}

      {showEndModal && (
        <EndContractModal
          contract={contract}
          onClose={() => setShowEndModal(false)}
          onDone={() => { setShowEndModal(false); loadContract(); showMsg('Contract ended successfully', 'success'); }}
        />
      )}

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-card shadow-elevated w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Delete Contract?</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {contract.project_name} — this cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-500 border border-gray-200">
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-red-500 flex items-center justify-center gap-2 disabled:opacity-60">
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractDetail;